import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { useAppDispatch } from "./store/hooks";
import { store, persistor, ENABLE_PERSISTENCE } from "./store";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import MonthView from "./components/Calendar";
import DayView from "./components/DayView";
import AddTodo from "./components/AddTodo";
import AddNote from "./components/AddNote";
import AddReminder from "./components/AddReminder";
import AddHabit from "./components/AddHabit";
import EditHabit from "./components/EditHabit";
import HabitDetails from "./components/HabitDetails";
import AddTracker from "./components/AddTracker";
import EditTracker from "./components/EditTracker";
import TrackerDetail from "./components/TrackerDetail";
import NoteDetail from "./components/NoteDetail";
import NoteDetailByURL from "./components/NoteDetailByURL";
import AddJournalEntry from "./components/AddJournalEntry";
import EditJournalEntry from "./components/EditJournalEntry";
import JournalDetails from "./components/JournalDetails";
import TodoList from "./components/TodoList";
import HabitList from "./components/HabitList";
import TrackerList from "./components/TrackerList";
import NoteList from "./components/NoteList";
import Login from "./components/Login";
import Register from "./components/Register";
import Home from "./components/Home";
import About from "./components/About";
import Downloads from "./components/Downloads";
import FAQ from "./components/FAQ";
import OtherApps from "./components/OtherApps";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsAndConditions from "./components/TermsAndConditions";
import Guide from "./components/Guide";
import {
  navigateToNextMonth,
  navigateToPreviousMonth,
} from "./utils/dateUtils";
import { isAuthenticatedAndValid, getStoredUser } from "./utils/auth";
import {
  setUserProfile,
  updateStreakSpark,
  logout,
} from "./store/slices/userSlice";
import { fetchTodos, clearTodos } from "./store/slices/todosSlice";
import { fetchNotes } from "./store/slices/notesSlice";
import { fetchReminders } from "./store/slices/remindersSlice";
import { fetchTrackers, clearTrackers } from "./store/slices/trackersSlice";
import {
  fetchJournalEntries,
  clearJournalEntries,
} from "./store/slices/journalSlice";
import {
  getHabitsWithCompletions,
  clearHabits,
} from "./store/slices/habitsSlice";
import { clearNotes } from "./store/slices/notesSlice";
import { clearReminders } from "./store/slices/remindersSlice";
import { requestNotificationPermission } from "./utils/notificationUtils";
import { useReminderNotifications } from "./utils/useReminderNotifications";
import { useDailyProgressNotifications } from "./utils/useDailyProgressNotifications";
import Profile from "./components/Profile";
import BlogPost from "./components/BlogPost";
import VerifyEmail from "./components/VerifyEmail";
import ScrollToTop from "./components/ScrollToTop";
import OfflineIndicator from "./components/OfflineIndicator";
import DebugContainer from "./components/DebugContainer";
import CanonicalUpdater from "./components/CanonicalUpdater";
import { useTheme } from "./hooks/useTheme";
import ToastContainer from "./components/ToastContainer";
import { ToastProvider, useToastContext } from "./components/ToastContext";
import { useTranslation } from "react-i18next";
import "./App.css";

// Component to handle toast container with context
const ToastContainerWithContext: React.FC = () => {
  const { toasts, removeToast } = useToastContext();
  return <ToastContainer toasts={toasts} removeToast={removeToast} />;
};

document.getElementById("hide-me")?.remove();

const AppContent: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const hasFetchedData = useRef(false);
  const { t } = useTranslation();

  // Domain redirection logic
  useEffect(() => {
    const allowedDomains = ["streakspark.in", "localhost"];
    const currentDomain = window.location.hostname;

    if (!allowedDomains.includes(currentDomain)) {
      const newUrl = `https://streakspark.in${window.location.pathname}${window.location.search}`;
      window.location.replace(newUrl);
    }
  }, []);

  // Initialize theme on app load
  useTheme();

  // Check authentication on app load
  useEffect(() => {
    const fetchAllData = async () => {
      // Prevent duplicate fetches due to React Strict Mode
      if (hasFetchedData.current) {
        return;
      }

      hasFetchedData.current = true;

      try {
        // Calculate date range for habits with completions (3 months ago to 1 month ahead)
        const now = new Date();
        const startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1)
          .toISOString()
          .split("T")[0];
        const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 31)
          .toISOString()
          .split("T")[0];

        // Fetch all data concurrently for better performance
        await Promise.all([
          dispatch(fetchTodos()),
          dispatch(fetchNotes()),
          dispatch(fetchReminders()),
          dispatch(fetchJournalEntries()),
          dispatch(
            fetchTrackers({
              startDate,
              endDate,
              isActive: true,
              withEntries: true,
            }),
          ),
          dispatch(getHabitsWithCompletions({ startDate, endDate })),
        ]);
      } catch (error) {
        console.error("Failed to fetch user data:", error);
        // Reset flag on error so we can retry
        hasFetchedData.current = false;
      }
    };

    const checkAuth = async () => {
      setLoading(true);
      try {
        if (isAuthenticatedAndValid()) {
          const storedUser = getStoredUser();
          if (storedUser) {
            // Restore user data to Redux store
            dispatch(
              setUserProfile({
                id: storedUser.id as any, // Cast to UUID type
                username: storedUser.username,
                email: storedUser.email,
                name: storedUser.name,
                password: "", // Don't store password
                createdAt: new Date().toISOString(), // Use current time as fallback
                sparkPoints: storedUser.sparkPoints || 0, // Use stored value or default to 0
                preferences: {
                  dailyReminderNotificationTime: "20:00", // Default value
                },
                encryptionKey: "", // Default encryption key
                isEmailVerified: storedUser.isEmailVerified || false, // Add email verification status
              }),
            );

            // Update streak spark data with total spark points
            dispatch(
              updateStreakSpark({
                totalSparkPoints: storedUser.sparkPoints || 0,
              }),
            );

            // Fetch all user data from API
            await fetchAllData();

            // Request notification permission for reminders
            await requestNotificationPermission();
          }
        } else {
          // Token is expired or user is not authenticated
          // Clear Redux store and redirect to login
          dispatch(clearTodos());
          dispatch(clearHabits());
          dispatch(clearTrackers());
          dispatch(clearNotes());
          dispatch(clearReminders());
          dispatch(clearJournalEntries());
          dispatch(logout());
          const currentPath = window.location.pathname + window.location.search;
          const publicPaths = [
            "/",
            "/guide",
            "/about",
            "/faq",
            "/other-apps",
            "/privacy",
            "/terms",
            "/login",
            "/register",
            "/downloads",
            "/blog",
          ];
          // Allow blog post detail pages without login
          const publicBlogDetail = /^\/blog\/[a-zA-Z0-9-]+$/.test(
            window.location.pathname,
          );
          const publicNotePaths =
            /^\/note\/[^/]+$/.test(window.location.pathname) ||
            /^\/notes\/n\/[^/]+$/.test(window.location.pathname); // Matches /note/{id} and /notes/n/{urlfriendlyname} patterns
          if (
            !publicPaths.includes(window.location.pathname) &&
            !publicNotePaths &&
            !publicBlogDetail
          ) {
            const returnUrl = encodeURIComponent(currentPath);
            navigate(`/login?returnUrl=${returnUrl}`);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [dispatch, navigate]);

  // Initialize reminder notifications
  useReminderNotifications();

  // Initialize daily progress notifications
  useDailyProgressNotifications();

  const handleDateSelect = (dateString: string) => {
    navigate(`/day/${dateString}`);
  };

  const handleNavigateMonth = (direction: "prev" | "next") => {
    setCurrentDate((prevDate) => {
      return direction === "prev"
        ? navigateToPreviousMonth(prevDate)
        : navigateToNextMonth(prevDate);
    });
  };

  const handleHeaderClick = () => {
    // Reset to current month and navigate to month view
    setCurrentDate(new Date());
    navigate("/app");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950 flex items-center justify-center overflow-x-hidden transition-colors duration-300">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-glow mb-6">
            <svg
              className="animate-spin w-8 h-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
            {t("common.loading")}
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400">
            {t("common.loadingDesc")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950 overflow-x-hidden overflow-y-auto transition-colors duration-300">
      <ScrollToTop />
      <CanonicalUpdater />
      <OfflineIndicator />
      <DebugContainer />
      <ToastContainerWithContext />
      <Routes>
        {/* Website routes START */}
        <Route path="/" element={<Home />} />
        <Route path="guide" element={<Guide />} />
        <Route path="about" element={<About />} />
        <Route path="downloads" element={<Downloads />} />
        <Route path="faq" element={<FAQ />} />
        <Route path="other-apps" element={<OtherApps />} />
        <Route path="privacy" element={<PrivacyPolicy />} />
        <Route path="terms" element={<TermsAndConditions />} />
        <Route path="login" element={<Login />} />
        <Route path="register" element={<Register />} />
        <Route path="verify-email" element={<VerifyEmail />} />
        <Route path="blogs/:blogurlfriendlyname" element={<BlogPost />} />
        {/* Website routes END */}
        {/* App routes START */}
        <Route
          path="month"
          element={
            <MonthView
              currentDate={currentDate}
              onDateSelect={handleDateSelect}
              onNavigateMonth={handleNavigateMonth}
              onHeaderClick={handleHeaderClick}
            />
          }
        />
        <Route
          path="app"
          element={
            <MonthView
              currentDate={currentDate}
              onDateSelect={handleDateSelect}
              onNavigateMonth={handleNavigateMonth}
              onHeaderClick={handleHeaderClick}
            />
          }
        />
        <Route path="day/:date" element={<DayView />} />
        <Route path="day/today" element={<DayView />} />
        <Route path="profile" element={<Profile />} />
        <Route path="todos" element={<TodoList />} />
        <Route path="habits" element={<HabitList />} />
        <Route path="trackers" element={<TrackerList />} />
        <Route path="notes" element={<NoteList />} />
        <Route path="add-todo" element={<AddTodo />} />
        <Route path="add-note" element={<AddNote />} />
        <Route path="add-reminder" element={<AddReminder />} />
        <Route path="add-habit" element={<AddHabit />} />
        <Route path="edit-habit/:id" element={<EditHabit />} />
        <Route path="habit/:id" element={<HabitDetails />} />
        <Route path="add-tracker" element={<AddTracker />} />
        <Route path="edit-tracker/:id" element={<EditTracker />} />
        <Route path="tracker/:id" element={<TrackerDetail />} />
        <Route path="note/:noteid" element={<NoteDetail />} />
        <Route path="notes/n/:URLfriendlyname" element={<NoteDetailByURL />} />
        <Route path="add-journal-entry" element={<AddJournalEntry />} />
        <Route path="edit-journal-entry/:date" element={<EditJournalEntry />} />
        <Route path="journal-details/:date" element={<JournalDetails />} />
        {/* App routes END */}
      </Routes>
    </div>
  );
};

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <Provider store={store}>
        {ENABLE_PERSISTENCE ? (
          <PersistGate loading={null} persistor={persistor!}>
            <ToastProvider>
              <Router>
                <AppContent />
              </Router>
            </ToastProvider>
          </PersistGate>
        ) : (
          <ToastProvider>
            <Router>
              <AppContent />
            </Router>
          </ToastProvider>
        )}
      </Provider>
    </I18nextProvider>
  );
}

export default App;
